import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import LoginForm from "./components/LoginForm";
import RegisterForm from "./components/RegisterForm";
import SEO from "@/components/base/SEO";

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

export default function AuthPage() {
  const [mode, setMode] = useState<"login" | "register">("login");
  const [installPrompt, setInstallPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [showInstallBanner, setShowInstallBanner] = useState(false);
  const [installed, setInstalled] = useState(false);

  useEffect(() => {
    const handler = (e: Event) => {
      e.preventDefault();
      setInstallPrompt(e as BeforeInstallPromptEvent);
      setShowInstallBanner(true);
    };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const handleInstall = async () => {
    if (!installPrompt) return;
    await installPrompt.prompt();
    const { outcome } = await installPrompt.userChoice;
    if (outcome === "accepted") { setInstalled(true); setShowInstallBanner(false); }
  };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row bg-white">
      <SEO
        title="Sign In / Register — StarBookPrime"
        description="Sign in or create your StarBookPrime account with email or phone number."
        canonical="/auth"
        noIndex={true}
      />

      {/* Left Panel - Visual (desktop only) */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <img
          src="https://public.readdy.ai/ai/img_res/29b6fe00df5505fbd4ca8615d1eadcf7.jpg"
          alt="StarBookPrime Community"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-orange-900/70 via-orange-800/50 to-black/60" />
        <div className="absolute inset-0 flex flex-col justify-between p-12">
          <Link to="/" className="flex items-center gap-3">
            <img src="https://static.readdy.ai/image/3fc89005e5f8f541d0781d07714068dd/b31ed3570265bb91107b593ba40da4ad.jpeg" alt="StarBookPrime" className="h-12 w-12 rounded-xl object-cover" />
            <span className="text-white font-bold text-xl">StarBook<span className="text-orange-300">Prime</span></span>
          </Link>
          <div>
            <h2 className="text-4xl font-extrabold text-white leading-tight mb-4">
              Your Local<br />Marketplace &<br /><span className="text-orange-300">Social Hub</span>
            </h2>
            <p className="text-white/70 text-base leading-relaxed mb-8">
              Shop from verified factories, sell your products, or earn commissions as an influencer. All in one place.
            </p>
            <div className="grid grid-cols-3 gap-4">
              {[
                { icon: "ri-shield-check-line", label: "Verified Factories" },
                { icon: "ri-star-line", label: "Earn Commission" },
                { icon: "ri-smartphone-line", label: "Works on Phone" },
              ].map((f) => (
                <div key={f.label} className="bg-white/10 backdrop-blur-sm rounded-xl p-4 text-center">
                  <div className="w-8 h-8 flex items-center justify-center mx-auto mb-2">
                    <i className={`${f.icon} text-orange-300 text-xl`} />
                  </div>
                  <div className="text-white text-xs font-medium">{f.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Right Panel - Form */}
      <div className="flex-1 flex flex-col min-h-screen lg:min-h-0 bg-white overflow-y-auto">

        {/* Mobile top bar */}
        <div className="lg:hidden flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <Link to="/" className="flex items-center gap-2">
            <img src="https://static.readdy.ai/image/3fc89005e5f8f541d0781d07714068dd/b31ed3570265bb91107b593ba40da4ad.jpeg" alt="StarBookPrime" className="h-9 w-9 rounded-lg object-cover" />
            <span className="font-bold text-base text-gray-900">StarBook<span className="text-orange-500">Prime</span></span>
          </Link>
          <Link to="/" className="text-xs text-gray-500 flex items-center gap-1 hover:text-orange-500 transition-colors">
            <i className="ri-home-line" /> Home
          </Link>
        </div>

        {/* Install App Banner */}
        {showInstallBanner && !installed && (
          <div className="mx-4 mt-4 bg-gradient-to-r from-orange-500 to-amber-500 rounded-2xl p-4 flex items-center gap-3">
            <div className="w-10 h-10 flex items-center justify-center bg-white/20 rounded-xl flex-shrink-0">
              <i className="ri-smartphone-line text-white text-xl" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-white font-bold text-sm">Install StarBookPrime App</p>
              <p className="text-white/80 text-xs">Add to your home screen for the best experience</p>
            </div>
            <div className="flex gap-2 flex-shrink-0">
              <button onClick={handleInstall} className="px-3 py-2 bg-white text-orange-600 text-xs font-bold rounded-lg cursor-pointer whitespace-nowrap hover:bg-orange-50 transition-colors">
                Install
              </button>
              <button onClick={() => setShowInstallBanner(false)} className="w-7 h-7 flex items-center justify-center bg-white/20 rounded-lg text-white cursor-pointer hover:bg-white/30 transition-colors">
                <i className="ri-close-line text-sm" />
              </button>
            </div>
          </div>
        )}

        {/* iOS install hint (always show on mobile if not installed) */}
        <div className="lg:hidden mx-4 mt-3 bg-sky-50 border border-sky-200 rounded-xl px-4 py-3 flex items-start gap-3">
          <i className="ri-download-line text-sky-500 text-lg flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-xs font-bold text-sky-800">Install as App on Your Phone</p>
            <p className="text-xs text-sky-600 mt-0.5">
              <strong>iPhone:</strong> Tap <i className="ri-share-line" /> Share → &quot;Add to Home Screen&quot; &nbsp;|&nbsp;
              <strong>Android:</strong> Tap <i className="ri-more-2-line" /> Menu → &quot;Add to Home Screen&quot;
            </p>
          </div>
        </div>

        <div className="flex-1 flex flex-col justify-center px-5 py-8 lg:px-16">
          <div className="max-w-md w-full mx-auto">

            {/* Desktop logo */}
            <div className="hidden lg:flex items-center gap-2 mb-8">
              <Link to="/" className="flex items-center gap-2">
                <img src="https://static.readdy.ai/image/3fc89005e5f8f541d0781d07714068dd/b31ed3570265bb91107b593ba40da4ad.jpeg" alt="StarBookPrime" className="h-9 w-9 rounded-lg object-cover" />
                <span className="font-bold text-lg text-gray-900">StarBook<span className="text-orange-500">Prime</span></span>
              </Link>
            </div>

            <div className="mb-6">
              <h1 className="text-2xl font-extrabold text-gray-900 mb-1">
                {mode === "login" ? "Welcome back!" : "Join StarBookPrime"}
              </h1>
              <p className="text-sm text-gray-500">
                {mode === "login" ? "Sign in with email or phone number" : "Create your free account — email or phone"}
              </p>
            </div>

            {/* Social Login */}
            <div className="grid grid-cols-3 gap-3 mb-5">
              {[
                { icon: "ri-google-fill", label: "Google", color: "text-red-500" },
                { icon: "ri-facebook-fill", label: "Facebook", color: "text-sky-600" },
                { icon: "ri-apple-fill", label: "Apple", color: "text-gray-900" },
              ].map((s) => (
                <button key={s.label} type="button" className="flex items-center justify-center gap-2 py-3 border border-gray-200 rounded-xl text-sm font-semibold text-gray-600 hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap">
                  <i className={`${s.icon} ${s.color} text-lg`} />
                  <span className="hidden sm:inline text-xs">{s.label}</span>
                </button>
              ))}
            </div>

            <div className="flex items-center gap-3 mb-5">
              <div className="flex-1 h-px bg-gray-200" />
              <span className="text-xs text-gray-400 whitespace-nowrap">or use email / phone</span>
              <div className="flex-1 h-px bg-gray-200" />
            </div>

            {mode === "login" ? (
              <LoginForm onSwitch={() => setMode("register")} />
            ) : (
              <RegisterForm onSwitch={() => setMode("login")} />
            )}

            {/* App store badges */}
            <div className="mt-8 pt-6 border-t border-gray-100">
              <p className="text-xs text-gray-400 text-center mb-3 font-medium">Also available as a mobile app</p>
              <div className="flex gap-3 justify-center">
                <div className="flex items-center gap-2 px-4 py-2.5 bg-gray-900 rounded-xl cursor-pointer hover:bg-gray-800 transition-colors">
                  <i className="ri-apple-fill text-white text-xl" />
                  <div>
                    <p className="text-white/60 text-xs leading-none">Download on the</p>
                    <p className="text-white font-bold text-sm leading-tight">App Store</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 px-4 py-2.5 bg-gray-900 rounded-xl cursor-pointer hover:bg-gray-800 transition-colors">
                  <i className="ri-google-play-fill text-white text-xl" />
                  <div>
                    <p className="text-white/60 text-xs leading-none">Get it on</p>
                    <p className="text-white font-bold text-sm leading-tight">Google Play</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
